import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as Popover from '@radix-ui/react-popover';
import * as Icons from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SHELL } from '@/constants/testIds';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/components/AuthProvider';

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function typeIcon(type) {
  switch (type) {
    case 'settlement_needs_review':
      return Icons.Wallet;
    case 'invoice_overdue':
      return Icons.AlertTriangle;
    case 'load_delayed':
      return Icons.Truck;
    case 'trip_pending':
      return Icons.Route;
    case 'bill_overdue':
      return Icons.Receipt;
    case 'ratecon_pending':
      return Icons.FileText;
    case 'import_completed':
      return Icons.Upload;
    case 'billing_event':
      return Icons.CreditCard;
    case 'user_invited':
      return Icons.UserPlus;
    default:
      return Icons.Bell;
  }
}

export function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user, ready } = useAuth();

  const notifsQ = useQuery({
    queryKey: ['notifications'],
    enabled: ready && Boolean(user) && user.role !== 'super_admin',
    staleTime: 30_000,
    queryFn: async () => {
      const r = await api('/api/v1/notifications');
      if (r.status !== 200) throw new Error('notifications');
      return r.data.notifications ?? [];
    },
    refetchInterval: 60_000,
  });

  const items = notifsQ.data ?? [];
  const unreadCount = items.filter((n) => !n.readAt).length;

  async function markRead(id) {
    const r = await api(`/api/v1/notifications/${id}/read`, { method: 'POST', body: {} });
    if (r.status < 200 || r.status >= 300) {
      toast.error(String(r.data?.error ?? 'Could not mark read'));
      return;
    }
    qc.setQueryData(['notifications'], (prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((n) =>
        n.id === id ? { ...n, readAt: r.data?.notification?.readAt || new Date().toISOString() } : n
      );
    });
  }

  async function markAllRead() {
    const r = await api('/api/v1/notifications/read-all', { method: 'POST', body: {} });
    if (r.status < 200 || r.status >= 300) {
      // Fallback: mark one-by-one for older API
      const unread = items.filter((n) => !n.readAt);
      for (const n of unread) {
        await api(`/api/v1/notifications/${n.id}/read`, { method: 'POST', body: {} });
      }
    }
    await qc.invalidateQueries({ queryKey: ['notifications'] });
  }

  function openItem(n) {
    if (!n.readAt) void markRead(n.id);
    const href = n.payload?.href;
    if (typeof href === 'string' && href.startsWith('/')) {
      navigate(href);
    }
  }

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid={SHELL.notificationBell}
          className="relative"
          aria-label={unreadCount ? `${unreadCount} unread notifications` : 'Notifications'}
        >
          <Icons.Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-destructive text-[10px] font-bold text-white flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="w-96 bg-popover border rounded-lg shadow-lg z-50 overflow-hidden"
          align="end"
          sideOffset={8}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h3 className="font-semibold text-sm">Notifications</h3>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="text-xs text-primary hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto custom-scrollbar">
            {notifsQ.isLoading ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : items.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground px-4">
                No alerts right now. Overdue invoices, delayed loads, pending trips, and items
                needing review will appear here.
              </div>
            ) : (
              items.map((n) => {
                const unread = !n.readAt;
                const Icon = typeIcon(n.type);
                return (
                  <button
                    type="button"
                    key={n.id}
                    className={`w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-accent transition-colors ${
                      unread ? 'bg-primary/5' : ''
                    }`}
                    onClick={() => openItem(n)}
                  >
                    <div className="flex items-start gap-2">
                      <Icon
                        className={`h-4 w-4 mt-0.5 shrink-0 ${
                          unread ? 'text-primary' : 'text-muted-foreground'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium truncate">{n.title}</p>
                          {unread && (
                            <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
                        <p className="text-xs text-muted-foreground mt-1">{timeAgo(n.createdAt)}</p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
